import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';
import { createWrapperPayload, docsWrapperConstants, wrapperConstants } from '../wrapper-api';

export const fetchInstallationHistoryData = createAsyncThunk(
  'installationHistory/fetchInstallationHistoryData',
  async (wellId: number | undefined) => {
    if (!wellId) {
      return [];
    }
    try {
      const requestPayload = createWrapperPayload(
        'GET',
        `/pumpchecker/installation/GetInstallationsByWell?wellId=${wellId}`,
      );

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response && response.data) {
          // Handle different possible response structures
          if (response.data.values) {
            return response.data.values;
          } else if (response.data.Data) {
            return response.data.Data;
          } else if (Array.isArray(response.data)) {
            return response.data;
          } else {
            return response.data;
          }
        } else {
          return [];
        }
      });
    } catch (error) {
      console.error('Error fetching Installation History Data:', error);
      return [];
    }
  },
);

export const fetchAddInstallationData = createAsyncThunk(
  'installs/fetchAddInstallationData',
  async (wellId: number) => {
    try {
      const requestPayload = createWrapperPayload(
        'GET',
        `/pumpchecker/Installation/AddInstallationData?wellId=${wellId}`,
      );

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error fetching add installation data:', error);
      return {};
    }
  },
);

export const fetchEditInstallationData = createAsyncThunk(
  'installs/fetchEditInstallationData',
  async ({ wellId, id }: { wellId: number; id: number }) => {
    try {
      const requestPayload = createWrapperPayload(
        'GET',
        `/pumpchecker/Installation/EditInstallationData?id=${id}&wellId=${wellId}`,
      );

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error fetching edit installation data:', error);
      return {};
    }
  },
);

export const fetchGetWells = createAsyncThunk('installs/fetchGetWells', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetWellLookups`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching get wells data:', error);
    return {};
  }
});

export const fetchGetPullReasonsData = createAsyncThunk('installs/fetchGetPullReasonsData', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetPullReasons`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching get pull reasons data:', error);
    return {};
  }
});

export const fetchGetComponentsData = createAsyncThunk('installs/fetchGetComponentsData', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetComponents`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching get components data:', error);
    return {};
  }
});

export const fetchGetSubcomponentsData = createAsyncThunk(
  'installs/fetchGetSubcomponentsData',
  async (component: string) => {
    try {
      const requestPayload = createWrapperPayload(
        'GET',
        `/pumpchecker/Lookup/GetSubcomponentsByComponent?component=${component}&filter%5Bfilters%5D%5B0%5D%5Bfield%5D=Name&filter%5Bfilters%5D%5B0%5D%5Boperator%5D=eq&filter%5Bfilters%5D%5B0%5D%5Bvalue%5D=${component}%20String&filter%5Blogic%5D=and`,
      );

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error fetching get subcomponents data:', error);
      return {};
    }
  },
);

export const fetchGetFailureCausesData = createAsyncThunk('installs/fetchGetFailureCausesData', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetFailureCauses`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching get failure causes data:', error);
    return {};
  }
});

export const fetchGetFailureCauseDetailsData = createAsyncThunk(
  'installs/fetchGetFailureCauseDetailsData',
  async () => {
    try {
      const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetFailureCauseDetails`);

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error fetching get failure cause details data:', error);
      return {};
    }
  },
);

export const fetchGetObservationsData = createAsyncThunk(
  'installs/fetchGetObservationsData',
  async (_, { rejectWithValue }) => {
    try {
      const formData = new URLSearchParams();
      formData.append('sort', '');
      formData.append('group', 'Component-asc');
      formData.append('filter', '');

      const requestPayload = createWrapperPayload(
        'POST',
        `/pumpchecker/Admin/GetObservations`,
        formData.toString(),
        true,
      );

      const response = await api.post(wrapperConstants.router, requestPayload);
      if (response && response.data) {
        return response.data;
      } else {
        return rejectWithValue('No data returned');
      }
    } catch (error) {
      console.error('Error fetching observations data:', error);
      let message = 'Unknown error';
      if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
        message = (error as any).message;
      }
      return rejectWithValue(message);
    }
  },
);

export const editInstallation = createAsyncThunk(
  'installs/editInstallation',
  async (
    {
      payload,
      documents,
    }: {
      payload: Record<string, any>;
      documents?: {
        InstallReport?: File[];
        PullReport?: File[];
        RcfaReport?: File[];
        CompletionDiagram?: File[];
        DesignDataSheet?: File[];
        VendorDesign?: File[];
        VendorQuote?: File[];
      };
    },
    { rejectWithValue },
  ) => {
    try {
      // multipart/form-data
      const formData = new FormData();

      formData.append('ApiApplicationName', 'PumpChecker');
      formData.append('CallType', 'POST');
      formData.append('URL', '/pumpchecker/Installation/EditInstallation?redirect=false');
      formData.append('AssetId', '');
      formData.append('IsInstall', 'true');

      if (documents) {
        const docFields = [
          { key: 'InstallReport', formKey: 'Documents.InstallReport' },
          { key: 'PullReport', formKey: 'Documents.PullReport' },
          { key: 'RcfaReport', formKey: 'Documents.RcfaReport' },
          { key: 'CompletionDiagram', formKey: 'Documents.CompletionDiagram' },
          { key: 'DesignDataSheet', formKey: 'Documents.DesignDataSheet' },
          { key: 'VendorDesign', formKey: 'Documents.VendorDesign' },
          { key: 'VendorQuote', formKey: 'Documents.VendorQuote' },
        ] as const;
        docFields.forEach(({ key, formKey }) => {
          const files = documents[key];
          if (files && Array.isArray(files) && files.length > 0) {
            files.forEach((file) => {
              formData.append(formKey, file, file.name);
            });
          }
        });
      }

      const payloadData = new URLSearchParams();
      Object.entries(payload).forEach(([key, value]) => {
        payloadData.append(key, value?.toString() ?? '');
      });
      payloadData.append('sort', '');
      payloadData.append('group', '');
      payloadData.append('filter', '');

      formData.append('Payload', payloadData.toString());

      const response = await api.post(docsWrapperConstants.router, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message || 'Unknown error');
    }
  },
);

export const fetchWellIdentifier = createAsyncThunk('wellTestData/fetchWellIdentifier', async (assetId: string) => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Well/GetWellId?externalIdentifier=${assetId}`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return null;
      }
    });
  } catch (error) {
    console.error('Error fetching well test ID:', error);
    return null;
  }
});

export const deleteInstallation = createAsyncThunk('installs/deleteInstallation', async (id: string) => {
  try {
    const requestPayload = createWrapperPayload(
      'GET',
      `/pumpchecker/Installation/DeleteInstallation?id=${id}&redirect=false`,
    );

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error deleting installation:', error);
    return {};
  }
});

export const updateObservationsData = createAsyncThunk(
  'installs/updateObservationsData',
  async ({ id, observationIds }: { id?: number; observationIds: number[] }, { rejectWithValue }) => {
    try {
      const formData = new URLSearchParams();
      formData.append('sort', '');
      formData.append('group', '');
      formData.append('filter', '');

      observationIds.forEach((observationId) => {
        formData.append('observationIds', observationId.toString());
      });

      const requestPayload = createWrapperPayload(
        'POST',
        `/pumpchecker/Installation/UpdateObservations/${id}`,
        formData.toString(),
        true,
      );

      const response = await api.post(wrapperConstants.router, requestPayload);
      if (response && response.data) {
        return response.data;
      } else {
        return rejectWithValue('No data returned');
      }
    } catch (error) {
      console.error('Error updating observations data:', error);
      let message = 'Unknown error';
      if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
        message = (error as any).message;
      }
      return rejectWithValue(message);
    }
  },
);
